import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

import { localDateStr, monthRange, fmtDate } from './utils';

export default function PerformanceReport() {
  const [offset, setOffset] = useState(0);
  const [fromDate, setFromDate] = useState(monthRange(0).from);
  const [toDate, setToDate] = useState(monthRange(0).to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (f, t) => {
    setLoading(true);
    fetch(`${API}/api/reports/performance?from_date=${f}&to_date=${t}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    const r = monthRange(offset);
    setFromDate(r.from); setToDate(r.to);
    load(r.from, r.to);
  }, [offset]);

  const applyCustom = () => load(fromDate, toDate);

  const exportCSV = () => {
    window.location.href = `${API}/api/reports/export/csv?type=performance&from_date=${fromDate}&to_date=${toDate}`;
  };

  const maxTickets = data ? Math.max(...data.members.map(m => m.tickets), 1) : 1;

  return (
    <div>
      <ReportNote what="Per-person ticket throughput and resolution times." from={fromDate} to={toDate} />
      {/* Controls */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setOffset(o => o - 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, minWidth: 140, textAlign: 'center' }}>
            {monthRange(offset).label}
          </span>
          <button onClick={() => setOffset(o => o + 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>›</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, borderLeft: '1px solid var(--border)' }}>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="input" style={{ width: 140 }} />
          <span style={{ color: 'var(--text-2)' }}>→</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="input" style={{ width: 140 }} />
          <button onClick={applyCustom} className="btn btn-primary" style={{ padding: '6px 14px' }}>Apply</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={exportCSV} className="btn btn-secondary">↓ CSV</button>
          <button onClick={() => window.print()} className="btn btn-secondary">🖨 Print / PDF</button>
        </div>
      </div>

      {loading && <div className="loading">Loading performance data…</div>}

      {!loading && data && (
        <>
          {/* Summary */}
          <div className="grid-3" style={{ marginBottom: 24 }}>
            <div className="card">
              <div className="card-title">Total Tickets Closed</div>
              <div className="card-value" style={{ color: 'var(--accent)' }}>{data.team_tickets}</div>
              <div className="card-sub">{fromDate} → {toDate}</div>
            </div>
            <div className="card">
              <div className="card-title">Total Hours Logged</div>
              <div className="card-value">{data.team_hours_display}</div>
              <div className="card-sub">excl. ABE &amp; ABS</div>
            </div>
            <div className="card">
              <div className="card-title">Active Members</div>
              <div className="card-value">{data.members.filter(m => m.tickets > 0).length}</div>
              <div className="card-sub">with tickets closed</div>
            </div>
          </div>

          {/* Table */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18 }}>
              Team Performance — {fmtDate(fromDate)} to {fmtDate(toDate)}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th style={{ textAlign: 'right' }}>Tickets Closed</th>
                    <th style={{ minWidth: 140 }}>Progress</th>
                    <th style={{ textAlign: 'right' }}>Hours Logged</th>
                    <th style={{ textAlign: 'right' }}>Avg Resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map((m, i) => (
                    <tr key={m.name}>
                      <td style={{ color: 'var(--text-3)', fontFamily: 'DM Mono, monospace', fontSize: 11 }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{m.name}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 700, fontSize: 16, color: 'var(--accent)' }}>
                        {m.tickets}
                      </td>
                      <td>
                        <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(m.tickets / maxTickets) * 100}%`, background: 'var(--accent)', borderRadius: 99 }} />
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--text-2)' }}>
                        {m.hours_display}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--text-2)' }}>
                        {m.avg_resol_display}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sidebar, .topbar { display: none !important; }
          .main { margin: 0 !important; padding: 20px !important; }
        }
      `}</style>
    </div>
  );
}
